import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgentType, Conversation, Message } from "../types";
import {
  CHAT_ACTIVE_CONVERSATION_ID_KEY,
  CHAT_RESET_NEXT_LOGIN_KEY,
  getConversationAgentTypes,
} from "@/core/utils/chatbot-storage";
import {
  deleteConversation,
  getConversationMessages,
  listConversations,
} from "../services/lia-conversation-history-service";
import { buildGreetingSuggestions } from "../utils/chatbotUtils";

/** Merges stored localStorage agent_types into the fetched conversation list. */
function enrichWithStoredAgentTypes(
  conversations: Conversation[],
): Conversation[] {
  const stored = getConversationAgentTypes();
  if (Object.keys(stored).length === 0) return conversations;
  return conversations.map((c) => ({
    ...c,
    agent_type: c.agent_type ?? (stored[c.id] as AgentType | undefined) ?? null,
  }));
}

type UseLiaChatHistoryDbMockArgs = {
  companyId?: string | number | null;
  userId?: string | number | null;
  greeting: Message;
  greetingTitle?: string;
  /**
   * Quando true, a hidratação inicial NÃO restaura a última conversa: monta
   * sempre no estado welcome (conversa nova), mantendo a lista de conversas
   * disponível para o painel de histórico. Usado pelo Centro de ações com IA.
   */
  startFresh?: boolean;
};

function isResetNextLoginFlagEnabled() {
  try {
    return localStorage.getItem(CHAT_RESET_NEXT_LOGIN_KEY) === "true";
  } catch {
    return false;
  }
}

function consumeResetNextLoginFlag() {
  try {
    localStorage.removeItem(CHAT_RESET_NEXT_LOGIN_KEY);
  } catch {
    // ignore
  }
}

function getSessionActiveConversationId() {
  try {
    return sessionStorage.getItem(CHAT_ACTIVE_CONVERSATION_ID_KEY);
  } catch {
    return null;
  }
}

function setSessionActiveConversationId(conversationId: string | null) {
  try {
    if (!conversationId) {
      sessionStorage.removeItem(CHAT_ACTIVE_CONVERSATION_ID_KEY);
      return;
    }
    sessionStorage.setItem(CHAT_ACTIVE_CONVERSATION_ID_KEY, conversationId);
  } catch {
    // ignore
  }
}

function normalizeGreetingMessage({
  greeting,
  greetingContent,
  greetingSuggestions,
}: {
  greeting: Message;
  greetingContent: string;
  greetingSuggestions: ReturnType<typeof buildGreetingSuggestions>;
}): Message {
  return {
    ...greeting,
    content: greetingContent,
    suggestions: greetingSuggestions,
    timestamp: new Date(),
  };
}

export function useLiaChatHistoryDbMock({
  companyId,
  userId,
  greeting,
  startFresh = false,
}: UseLiaChatHistoryDbMockArgs) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [hydrated, setHydrated] = useState(false);

  const [translate] = useTranslation(["core"]);
  const isHydratingRef = useRef(false);

  const greetingSuggestions = useMemo(() => {
    // O hub picker de agentes (AgentHubPicker) substitui os cards de sugestão
    // categorizados no estado welcome. Mantemos o greeting sem suggestions para
    // não duplicar os mesmos botões caso o bubble do greeting seja renderizado.
    return [];
  }, []);

  const greetingContent = useMemo(() => {
    return (translate("chatbot-greeting") as string) ?? "";
  }, [translate]);

  const buildNormalizedGreeting = useCallback(() => {
    return normalizeGreetingMessage({
      greeting,
      greetingContent,
      greetingSuggestions,
    });
  }, [greeting, greetingContent, greetingSuggestions]);

  const applyGreetingMeta = useCallback(
    (rawMessages: Message[]): Message[] => {
      return rawMessages.map((m) => {
        if (m.id !== greeting.id) return m;

        return {
          ...m,
          content: greetingContent,
          suggestions: greetingSuggestions,
        };
      });
    },
    [greeting.id, greetingContent, greetingSuggestions],
  );

  const hydrateFromDb = useCallback(async () => {
    if (!companyId || !userId) return;
    if (isHydratingRef.current) return;

    isHydratingRef.current = true;
    setHydrated(false);

    try {
      const rawList = await listConversations({ companyId });
      const list = enrichWithStoredAgentTypes(rawList);

      setConversations(list);

      const resetNextLogin = isResetNextLoginFlagEnabled();
      if (resetNextLogin || startFresh) {
        if (resetNextLogin) consumeResetNextLoginFlag();

        const normalizedGreeting = buildNormalizedGreeting();
        setActiveConversationId(null);
        // startFresh não apaga o id salvo na sessão: outras superfícies
        // (painel docked) continuam restaurando a última conversa.
        if (resetNextLogin) setSessionActiveConversationId(null);
        setMessages([normalizedGreeting]);
        setHydrated(true);
        return;
      }

      const savedActive = getSessionActiveConversationId();
      const savedExists = savedActive
        ? list.some((c) => c.id === savedActive)
        : false;

      const initialConversationId = savedExists
        ? savedActive
        : (list[0]?.id ?? null);

      if (!initialConversationId) {
        setActiveConversationId(null);
        setSessionActiveConversationId(null);
        setMessages([buildNormalizedGreeting()]);
        setHydrated(true);
        return;
      }

      setActiveConversationId(initialConversationId);
      setSessionActiveConversationId(initialConversationId);

      const msgs = await getConversationMessages({
        companyId,
        conversationId: initialConversationId,
      });
      const normalized = applyGreetingMeta(msgs);
      setMessages(normalized.length ? normalized : [buildNormalizedGreeting()]);
      setHydrated(true);
    } catch {
      // Em caso de erro de rede, exibe a tela de welcome para que o usuário
      // possa continuar usando o chat — não deixa o input travado.
      setMessages([buildNormalizedGreeting()]);
      setHydrated(true);
    } finally {
      isHydratingRef.current = false;
    }
  }, [
    companyId,
    userId,
    startFresh,
    buildNormalizedGreeting,
    applyGreetingMeta,
  ]);

  useEffect(() => {
    hydrateFromDb();
  }, [hydrateFromDb]);

  const selectConversation = useCallback(
    async (conversationId: string) => {
      if (!companyId || !userId) return;
      if (conversationId === activeConversationId) return;

      setHydrated(false);
      setActiveConversationId(conversationId);
      setSessionActiveConversationId(conversationId);

      const msgs = await getConversationMessages({ companyId, conversationId });
      const normalized = applyGreetingMeta(msgs);
      setMessages(normalized.length ? normalized : [buildNormalizedGreeting()]);
      setHydrated(true);
    },
    [
      companyId,
      userId,
      activeConversationId,
      applyGreetingMeta,
      buildNormalizedGreeting,
    ],
  );

  const createNewConversation = useCallback(async () => {
    if (!companyId || !userId) return;

    setHydrated(false);
    const normalizedGreeting = buildNormalizedGreeting();
    setActiveConversationId(null);
    setSessionActiveConversationId(null);
    setMessages([normalizedGreeting]);
    setHydrated(true);
  }, [companyId, userId, buildNormalizedGreeting]);

  const refreshConversations = useCallback(async () => {
    if (!companyId || !userId) return;
    const rawList = await listConversations({ companyId });
    setConversations(enrichWithStoredAgentTypes(rawList));
  }, [companyId, userId]);

  const removeConversation = useCallback(
    async (conversationId: string) => {
      if (!companyId || !userId) return;
      await deleteConversation({ companyId, conversationId });

      const rawList = await listConversations({ companyId });
      const list = enrichWithStoredAgentTypes(rawList);

      setConversations(list);

      if (conversationId === activeConversationId) {
        if (list[0]?.id) {
          await selectConversation(list[0].id);
          return;
        }

        const normalizedGreeting = buildNormalizedGreeting();
        setActiveConversationId(null);
        setSessionActiveConversationId(null);
        setMessages([normalizedGreeting]);
        setConversations([]);
        setHydrated(true);
      }
    },
    [
      companyId,
      userId,
      activeConversationId,
      buildNormalizedGreeting,
      selectConversation,
    ],
  );

  return {
    conversations,
    setConversations,
    activeConversationId,
    messages,
    setMessages,
    hydrated,
    actions: {
      selectConversation,
      createNewConversation,
      removeConversation,
      refreshConversations,
    },
  };
}
